/**
 * React's view of the session in src/api/session.ts.
 *
 * The session lives outside React because the API client reads it on every
 * request, and a request can start before any component renders. This hook
 * subscribes to it so a screen re-renders when it changes.
 */

import { useSyncExternalStore } from "react";

import { getActiveClinic, isSignedIn, subscribe, type ActiveClinic } from "./api/session";

interface SessionView {
  signedIn: boolean;
  clinic: ActiveClinic | null;
}

// useSyncExternalStore compares snapshots by identity, so a new object on every
// read would loop forever. This one is rebuilt only when something changed.
let snapshot: SessionView = { signedIn: isSignedIn(), clinic: getActiveClinic() };

function read(): SessionView {
  const signedIn = isSignedIn();
  const clinic = getActiveClinic();
  if (snapshot.signedIn !== signedIn || snapshot.clinic?.id !== clinic?.id) {
    snapshot = { signedIn, clinic };
  }
  return snapshot;
}

export function useSession(): SessionView {
  return useSyncExternalStore(subscribe, read, read);
}
